import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  AppstoreOutlined,
  SolutionOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  ManpowerSummaryData,
  fmtINR,
  fmtINRCompact,
  num,
} from "./manpowerShared";

// KPI cards, style copied from MaterialKpiCards — compact ₹ value with a
// Tooltip carrying the exact fmtINR amount. Four cards: total, the PRW/Others
// split (with % shares), and the distinct labour item count.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ManpowerKpiCards: React.FC<{
  data?: ManpowerSummaryData | null;
  grandTotal: number;
}> = ({ data, grandTotal }) => {
  const items = data?.items || [];
  const itemCount = num(data?.itemCount) || items.length;
  const prwCost = num(data?.prwCost);
  const othersCost = num(data?.othersCost);
  const sharePct = (cost: number) =>
    grandTotal > 0 ? `${((cost / grandTotal) * 100).toFixed(1)}% of total` : "—";

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f6ffed", "#b7eb8f")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#389e0d")}>
            <WalletOutlined /> Total Manpower Cost
          </div>
          <Tooltip title={fmtINR(grandTotal)}>
            <div style={valueStyle}>{fmtINRCompact(grandTotal)}</div>
          </Tooltip>
          <div style={subStyle}>PRW norms + task resources</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f0f7ff", "#bae0ff")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#1677ff")}>
            <TeamOutlined /> PRW Cost
          </div>
          <Tooltip title={fmtINR(prwCost)}>
            <div style={valueStyle}>{fmtINRCompact(prwCost)}</div>
          </Tooltip>
          <div style={subStyle}>{sharePct(prwCost)}</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#fff7e6", "#ffd591")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#fa8c16")}>
            <SolutionOutlined /> Others Cost
          </div>
          <Tooltip title={fmtINR(othersCost)}>
            <div style={valueStyle}>{fmtINRCompact(othersCost)}</div>
          </Tooltip>
          <div style={subStyle}>{sharePct(othersCost)}</div>
        </Card>
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <Card
          size="small"
          style={cardStyle("#f9f4ff", "#d3adf7")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle("#722ed1")}>
            <AppstoreOutlined /> Distinct Items
          </div>
          <div style={valueStyle}>{itemCount}</div>
          <div style={subStyle}>
            {itemCount === 1 ? "labour item" : "labour items"} in scope
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default ManpowerKpiCards;
